import { Grid } from '@mui/material'
import { Button } from 'global/components/Button'
import { FormField } from 'global/components/Form'
import { Link } from 'global/components/Link'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { ExternalLinkIcon } from 'lucide-react'
import { useState } from 'react'
import {
    funcionario,
    TabelaHorarioFuncionario,
    tabelaHorarios,
    UpdateTabelaHorarioFuncionarioSchema,
} from 'service/api'
import { toast } from 'sonner'

interface Props {
    open: boolean
    onClose: () => void
    tabela: TabelaHorarioFuncionario
    disableFields?: boolean
}

export function EditarTabelaHorarioFuncionario({
    onClose,
    open,
    tabela,
    disableFields = false,
}: Props) {
    const [isExcluirOpen, setExcluirOpen] = useState(false)

    const { mutateAsync, isLoading } =
        tabelaHorarios.funcionario.mutations.useUpdate({
            invalidateQuery: true,
        })

    if (!tabela) return null

    function handleSubmit(values: any) {
        return mutateAsync(values, {
            onSuccess: () => {
                toast.success('Funcionário atualizado com sucesso!')
                funcionario.invalidateQueries()
            },

            onError: err => {
                toast.error(err.message)
            },
        })
    }

    const disabled = tabela.isTrocaAutomatica || disableFields

    return [
        <Dialog.Root
            open={hasPermission(['update', 'Funcionários']) && open}
            width="40rem"
            height="auto"
            onClose={onClose}
            onSubmit={handleSubmit}
            loading={isLoading}
            defaultValues={tabela}
            schema={UpdateTabelaHorarioFuncionarioSchema}
            disableFields={disabled}
        >
            <Dialog.Title>
                <Link
                    href={`/cadastros/tabela-horarios/${tabela.tabelaHorarioId}/editar`}
                    className="inline-flex items-center gap-1"
                    title="Ir para tabela de horário"
                >
                    {tabela.TabelaHorario.nome}&nbsp;
                    <ExternalLinkIcon className="size-4" />
                </Link>
                {tabela.isTrocaAutomatica &&
                    hasPermission(
                        ['read', 'TrocaAutomáticaDeHorário'],
                        () => (
                            <Link
                                className="text-sm ml-2 opacity-50 text-inherit hover:underline inline-flex items-center gap-1"
                                href="/manutencao-ponto/troca-automatica-horario"
                                title="Ir para troca automática de horário"
                            >
                                <span>(Troca automática)</span>
                                <ExternalLinkIcon className="size-4" />
                            </Link>
                        ),
                        () => (
                            <span className="text-sm ml-2 opacity-50">
                                (Troca automática)
                            </span>
                        ),
                    )}
            </Dialog.Title>
            <Dialog.Content>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <FormField
                            label="Data inicial"
                            name="dtInicial"
                            type="date"
                            required
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormField
                            label="Data final"
                            name="dtFinal"
                            type="date"
                            helperText={
                                disabled
                                    ? ''
                                    : 'Deixe em branco para indeterminado'
                            }
                        />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                {!disabled && (
                    <Button color="error" onClick={() => setExcluirOpen(true)}>
                        Excluir
                    </Button>
                )}
                {!disabled && <Dialog.Actions.PrimaryButton closeOnSubmit />}
            </Dialog.Actions>
        </Dialog.Root>,
        <ExcluirTabelaHorarioFuncionario
            open={isExcluirOpen}
            onClose={() => {
                setExcluirOpen(false)
                onClose()
            }}
            tabela={tabela}
        />,
    ]
}

function ExcluirTabelaHorarioFuncionario({ onClose, open, tabela }: Props) {
    const { mutateAsync, isLoading } =
        tabelaHorarios.funcionario.mutations.useDelete({
            invalidateQuery: true,
        })

    function handleSubmit() {
        return mutateAsync(Number(tabela.id), {
            onSuccess: () => {
                toast.success('Tabela removida com sucesso!')
                funcionario.invalidateQueries()
            },

            onError: err => {
                toast.error(err.message)
            },
        })
    }

    return (
        <Dialog.Root
            open={hasPermission(['update', 'Funcionários']) && open}
            width="auto"
            height="auto"
            onClose={onClose}
            onSubmit={handleSubmit}
            loading={isLoading}
        >
            <Dialog.Content>
                Tem certeza que deseja remover a tabela de horário do
                funcionário?
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton color="error" closeOnSubmit>
                    Excluir
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}
